import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, ExternalLink } from 'lucide-react';
// Static corporate command centre data (synced from use-case/ather/ather-data-requirements.json).
import data from '../../data/atherDataRequirements.json';
import {
  formatMaterialCostCr,
  getAverageChinaExposurePercent,
  loadCorporateProfile,
} from '../../utils/corporateProfileStorage';

// ----------------------------------------------------------------------------
// Lookups + label maps (minimal palette: gray + one accent)
// ----------------------------------------------------------------------------
const MATERIAL_LOOKUP = Object.fromEntries(data.materials.map((m) => [m.id, m]));
const COMPETITOR_LOOKUP = Object.fromEntries(data.competitors.map((c) => [c.id, c]));

const getCollection = (screenId, name) => {
  const screen = data.screens.find((s) => s.id === screenId);
  return screen?.data_collections.find((c) => c.name === name);
};
const sample = (screenId, name) => getCollection(screenId, name)?.sample_data || [];
const screenById = (id) => data.screens.find((s) => s.id === id);

const SEV_DOT = {
  RED: 'bg-red-500',
  YELLOW: 'bg-amber-500',
  GREEN: 'bg-emerald-500',
};
const SEV_LABEL = {
  RED: 'Critical',
  YELLOW: 'Warning',
  GREEN: 'Informational',
};

const RISK_DOT = {
  elevated: 'bg-amber-500',
  monitored: 'bg-blue-500',
  clear: 'bg-emerald-500',
};
const RISK_LABEL = {
  elevated: 'Elevated risk',
  monitored: 'Monitored',
  clear: 'Clear',
};

const CATEGORY_LABEL = {
  policy_regulatory: 'Policy',
  news_nlp: 'News',
  pricing: 'Pricing',
  geopolitical_risk: 'Geopolitical',
};

const ACTION_LABEL = {
  hedge: 'Hedge',
  optimise_procurement_timing: 'Time procurement',
  negotiate_contracts: 'Negotiate',
};

const SIGNAL_LABEL = {
  battery_supplier_announcement: 'Supplier announcement',
  pli_filing_or_approval: 'PLI approval',
  drhp_or_annual_report_disclosure: 'Disclosure',
  jv_with_mining_company: 'JV',
  chemistry_shift: 'Chemistry shift',
  lithium_offtake_lock_in: 'Lithium offtake',
  sodium_ion_shift: 'Sodium-ion shift',
};

const fmtPct = (v, signed = false) => {
  if (!Number.isFinite(v)) return '–';
  const s = signed && v > 0 ? '+' : '';
  return `${s}${v}%`;
};
const fmtInrCr = (v) => `₹${v} Cr`;
const fmtMoney = (price, currency, unit) =>
  `${currency === 'USD' ? '$' : ''}${price.toLocaleString()}${unit || ''}`;
const fmtMat = (id) => MATERIAL_LOOKUP[id]?.label || id;
const formatLabel = (s) =>
  typeof s === 'string'
    ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '';

// ----------------------------------------------------------------------------
// Primitives
// ----------------------------------------------------------------------------
const Card = ({ children, className = '' }) => (
  <div
    className={`bg-white dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-800 ${className}`}
  >
    {children}
  </div>
);

const Tag = ({ children, dot, className = '' }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/30 whitespace-nowrap ${className}`}
  >
    {dot ? <span className={`h-1.5 w-1.5 rounded-full ${dot}`} /> : null}
    {children}
  </span>
);

const MutedLabel = ({ children, className = '' }) => (
  <div
    className={`text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-500 ${className}`}
  >
    {children}
  </div>
);

const SectionHeader = ({ screen }) => (
  <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-800">
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
      {screen.title}
    </h2>
    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
      {[screen.persona, screen.cadence].filter(Boolean).join(' · ')}
    </div>
  </div>
);

const Delta = ({ value }) => {
  if (!Number.isFinite(value)) return null;
  const up = value >= 0;
  const cls = up
    ? 'text-red-600 dark:text-red-400'
    : 'text-emerald-600 dark:text-emerald-400';
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-sm font-medium tabular-nums ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {fmtPct(Math.abs(value), false)}
    </span>
  );
};

// ----------------------------------------------------------------------------
// HERO + KPI strip
// ----------------------------------------------------------------------------
const Hero = ({ profile } = {}) => {
  const { meta } = data;
  const storedProfile = profile ?? loadCorporateProfile();
  const hasStoredProfile = Boolean(storedProfile?.companyLegalName?.trim());

  const company = hasStoredProfile
    ? storedProfile.companyLegalName.trim()
    : meta.customer;

  const description = hasStoredProfile
    ? [
        storedProfile.companyMakes,
        storedProfile.annualUnitsLastFY &&
          `${Number(storedProfile.annualUnitsLastFY).toLocaleString('en-IN')} units (last FY)`,
        storedProfile.avgBatteryPackKwh &&
          `${storedProfile.avgBatteryPackKwh} kWh avg pack`,
      ]
        .filter(Boolean)
        .join(' · ')
    : meta.use_case;

  const chemistries = hasStoredProfile
    ? storedProfile.batteryChemistries?.filter(Boolean) ?? []
    : [meta.context_notes.battery_chemistry];

  const factories = hasStoredProfile
    ? (storedProfile.factoryStates || '')
        .split(/[,;]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    : meta.factory_locations;

  const motorType = storedProfile?.motorType?.trim();
  const annualCost = hasStoredProfile
    ? formatMaterialCostCr(storedProfile.totalAnnualMaterialCostCr)
    : `${meta.context_notes.material_cost_share_of_expenses_pct}%`;
  const chinaExposure = hasStoredProfile
    ? getAverageChinaExposurePercent(storedProfile)
    : meta.context_notes.china_lfp_cathode_share_pct;
  const fxRate = storedProfile?.fxBudgetRateUsd
    ? Number(storedProfile.fxBudgetRateUsd)
    : null;

  const heroStats = hasStoredProfile
    ? [
        annualCost
          ? { label: 'Annual material cost', value: annualCost }
          : null,
        chinaExposure != null
          ? { label: 'China-linked sourcing', value: `${chinaExposure}%` }
          : fxRate
            ? { label: 'FX budget rate', value: `₹${fxRate.toFixed(2)} / USD` }
            : formatMaterialCostCr(storedProfile?.quarterlyMaterialBudgetCr)
              ? {
                  label: 'Quarterly material budget',
                  value: formatMaterialCostCr(storedProfile.quarterlyMaterialBudgetCr),
                }
              : null,
      ].filter(Boolean)
    : [
        { label: 'Material cost share', value: `${meta.context_notes.material_cost_share_of_expenses_pct}%` },
        { label: 'China LFP cathode', value: `${meta.context_notes.china_lfp_cathode_share_pct}%+` },
      ];

  return (
    <Card className="p-7 md:p-8">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-3xl md:text-[34px] font-semibold text-gray-900 dark:text-white tracking-tight">
            {company}
          </h1>
          {description ? (
            <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-2xl text-[15px]">
              {description}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {chemistries.map((c) => (
              <Tag key={c}>{c} chemistry</Tag>
            ))}
            {factories.map((loc) => (
              <Tag key={loc}>{loc}</Tag>
            ))}
            {motorType ? <Tag>{motorType}</Tag> : null}
          </div>
        </div>
        {heroStats.length > 0 ? (
          <div className="flex gap-8 md:gap-10">
            {heroStats.map((stat) => (
              <div key={stat.label}>
                <MutedLabel>{stat.label}</MutedLabel>
                <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Card>
  );
};

const KpiStrip = () => {
  const k = data.meta.dashboard_kpis;
  const items = [
    { label: 'Critical alerts', value: k.active_red_alerts, dot: SEV_DOT.RED },
    { label: 'Warning alerts', value: k.active_yellow_alerts, dot: SEV_DOT.YELLOW },
    { label: 'China BOM share', value: `${k.china_bom_share_pct}%` },
    { label: 'At-risk routes', value: k.at_risk_routes, dot: RISK_DOT.elevated },
    { label: 'Annual material spend', value: `₹${k.annual_material_spend_inr_cr} Cr` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {items.map((it) => (
        <Card key={it.label} className="p-4">
          <div className="flex items-center gap-1.5">
            {it.dot ? <span className={`h-1.5 w-1.5 rounded-full ${it.dot}`} /> : null}
            <MutedLabel>{it.label}</MutedLabel>
          </div>
          <div className="text-2xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
            {it.value}
          </div>
        </Card>
      ))}
    </div>
  );
};

// ----------------------------------------------------------------------------
// SECTION 1 — Alerts
// ----------------------------------------------------------------------------
const AlertsSection = () => {
  const screen = screenById('screen_1');
  const alerts = sample('screen_1', 'live_alert_feed');
  const [sev, setSev] = useState('ALL');

  const counts = alerts.reduce((acc, a) => ({ ...acc, [a.severity]: (acc[a.severity] || 0) + 1 }), {});
  const filters = [
    { id: 'ALL', label: 'All', count: alerts.length },
    { id: 'RED', label: SEV_LABEL.RED, count: counts.RED || 0 },
    { id: 'YELLOW', label: SEV_LABEL.YELLOW, count: counts.YELLOW || 0 },
    { id: 'GREEN', label: SEV_LABEL.GREEN, count: counts.GREEN || 0 },
  ];
  const filtered = sev === 'ALL' ? alerts : alerts.filter((a) => a.severity === sev);

  return (
    <section id="alerts" className="scroll-mt-24">
      <SectionHeader screen={screen} />
      <Card>
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-1">
            {filters.map((f) => {
              const active = sev === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setSev(f.id)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    active
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {f.id !== 'ALL' ? (
                    <span className={`h-1.5 w-1.5 rounded-full ${SEV_DOT[f.id]}`} />
                  ) : null}
                  {f.label}
                  <span
                    className={`text-[10px] tabular-nums ${
                      active ? 'text-white/70 dark:text-gray-500' : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    {f.count}
                  </span>
                </button>
              );
            })}
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-500">
            Showing {filtered.length} of {alerts.length}
          </span>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {filtered.map((a) => (
            <div key={a.alert_id} className="px-5 py-4">
              <div className="flex items-start gap-3">
                <span
                  className={`mt-2 h-2 w-2 rounded-full flex-shrink-0 ${SEV_DOT[a.severity] || SEV_DOT.YELLOW}`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-500">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {SEV_LABEL[a.severity]}
                      </span>
                      <span>·</span>
                      <span>{CATEGORY_LABEL[a.category] || a.category}</span>
                      <span>·</span>
                      <span>{a.timestamp_ago}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-500">
                      <ExternalLink className="h-3 w-3" /> {a.source_name}
                    </span>
                  </div>
                  <h4 className="font-medium text-gray-900 dark:text-white text-[15px]">
                    {a.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                    {a.summary}
                  </p>
                  {a.estimated_impact ? (
                    <div className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                      Impact · {a.estimated_impact}
                    </div>
                  ) : null}
                  {a.material_ids?.length ? (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {a.material_ids.map((m) => (
                        <Tag key={m}>{fmtMat(m)}</Tag>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
};

// ----------------------------------------------------------------------------
// SECTION 2 — Costs
// ----------------------------------------------------------------------------
const CostsSection = () => {
  const screen = screenById('screen_2');
  const prices = sample('screen_2', 'commodity_prices');
  const costImpact = sample('screen_2', 'cost_impact_summary');
  const decomp = sample('screen_2', 'price_volume_decomposition');

  const decompData = decomp.map((d) => ({
    name: fmtMat(d.material_id).split(' ')[0],
    Price: d.price_effect_inr_cr,
    Volume: d.volume_effect_inr_cr,
  }));
  const totalImpact = costImpact.reduce((s, c) => s + c.cost_impact_inr_cr, 0);

  return (
    <section id="costs" className="scroll-mt-24">
      <SectionHeader screen={screen} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        {prices.map((p) => (
          <Card key={p.material_id} className="p-4">
            <MutedLabel>{fmtMat(p.material_id)}</MutedLabel>
            <div className="flex items-baseline justify-between mt-2">
              <div className="text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
                {fmtMoney(p.current_price, p.currency, p.unit)}
              </div>
              <Delta value={p.change_30d_pct} />
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-2 tabular-nums">
              7d {fmtPct(p.change_7d_pct, true)} · 30d {fmtPct(p.change_30d_pct, true)}
            </div>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Cost impact on Ather · Q3 FY26
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-800">
            {costImpact.map((c) => (
              <div key={c.material_id} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {fmtMat(c.material_id)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                    {c.narrative}
                  </div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-xs text-gray-500 dark:text-gray-500 tabular-nums">
                    {fmtPct(c.price_change_pct, true)}
                  </div>
                  <div className="text-base font-semibold text-gray-900 dark:text-white tabular-nums">
                    {fmtInrCr(c.cost_impact_inr_cr)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-800 flex items-baseline justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">Total quarterly impact</span>
            <span className="text-xl font-semibold text-gray-900 dark:text-white tabular-nums">
              {fmtInrCr(totalImpact)}
            </span>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Price vs volume decomposition
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-500">₹ Cr by material</span>
          </div>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={decompData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Bar dataKey="Price" stackId="a" fill="#0f172a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Volume" stackId="a" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 divide-y divide-gray-200 dark:divide-gray-800">
            {decomp.map((d) => (
              <div
                key={d.material_id}
                className="flex items-center justify-between text-xs py-2 first:pt-0"
              >
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                  {fmtMat(d.material_id)}
                </span>
                <span className="text-gray-500 dark:text-gray-500 tabular-nums">
                  Price {d.price_effect_pct}% · Volume {d.volume_effect_pct}% →{' '}
                  <span className="text-gray-900 dark:text-white font-medium">
                    {ACTION_LABEL[d.recommended_action_hint] || d.recommended_action_hint}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// SECTION 3 — China exposure
// ----------------------------------------------------------------------------
const ChinaSection = () => {
  const screen = screenById('screen_3');
  const bom = sample('screen_3', 'bom_country_concentration');
  const watchlist = sample('screen_3', 'china_policy_watchlist');
  const chinaShare = bom.find((b) => b.country_code === 'CN')?.bom_share_pct || 0;
  const tier2Share = bom.find((b) => b.country_code === 'IN')?.bom_share_pct || 0;
  const chartData = [...bom].sort((a, b) => b.bom_share_pct - a.bom_share_pct);

  return (
    <section id="china" className="scroll-mt-24">
      <SectionHeader screen={screen} />

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <Card className="p-5">
          <MutedLabel>China direct BOM share</MutedLabel>
          <div className="text-4xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
            {chinaShare}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1.5">
            LFP cathode, anode graphite, electrolyte, REE magnets
          </div>
        </Card>
        <Card className="p-5">
          <MutedLabel>Hidden tier-2 China content</MutedLabel>
          <div className="text-4xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
            +{tier2Share}%
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-500 mt-1.5">
            Indian suppliers whose raw materials still trace back to China
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-5 gap-4">
        <Card className="p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            BOM share by origin
          </h3>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart
                data={chartData}
                layout="vertical"
                margin={{ left: 20, right: 10, top: 5, bottom: 5 }}
              >
                <CartesianGrid stroke="currentColor" strokeOpacity={0.08} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  unit="%"
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="country_name"
                  tick={{ fontSize: 11 }}
                  width={140}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                />
                <Bar dataKey="bom_share_pct" name="BOM share %" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.country_code}
                      fill={entry.country_code === 'CN' ? '#0f172a' : '#cbd5e1'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="lg:col-span-3 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              China policy watchlist
            </h3>
            <span className="text-[11px] text-gray-500 dark:text-gray-500">
              {watchlist.length} entries
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                  <th className="py-2.5 px-5 font-medium">Date</th>
                  <th className="py-2.5 px-3 font-medium">Policy</th>
                  <th className="py-2.5 px-3 font-medium">Material</th>
                  <th className="py-2.5 px-3 font-medium">Status</th>
                  <th className="py-2.5 px-5 font-medium">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                {watchlist.map((w) => (
                  <tr key={w.entry_id}>
                    <td className="py-3 px-5 text-xs text-gray-500 dark:text-gray-500 whitespace-nowrap tabular-nums">
                      {w.announced_date}
                    </td>
                    <td className="py-3 px-3">
                      <div className="font-medium text-gray-900 dark:text-white text-[13px]">
                        {w.policy_title}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                        {w.what_changed}
                      </div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-600 mt-1">
                        {w.source_name} · Effective {w.effective_date}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex flex-wrap gap-1">
                        {w.material_ids?.map((mid) => (
                          <Tag key={mid}>{fmtMat(mid)}</Tag>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-700 dark:text-gray-300 capitalize">
                      {w.status}
                    </td>
                    <td className="py-3 px-5">
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                        <span className={`h-1.5 w-1.5 rounded-full ${SEV_DOT[w.severity]}`} />
                        {SEV_LABEL[w.severity]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// SECTION 4 — Plan B
// ----------------------------------------------------------------------------
const PlanBSection = () => {
  const screen = screenById('screen_4');
  const alts = sample('screen_4', 'alternative_supplier_shortlist');
  const india = sample('screen_4', 'india_domestic_sourcing_map');
  const materials = [...new Set(alts.map((a) => a.material_id))];
  const [mat, setMat] = useState(materials[0]);
  const rows = alts.filter((a) => a.material_id === mat).sort((a, b) => a.rank - b.rank);

  return (
    <section id="plan-b" className="scroll-mt-24">
      <SectionHeader screen={screen} />

      <Card className="mb-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Alternative supplier shortlist
          </h3>
          <div className="flex items-center gap-0.5 border border-gray-200 dark:border-gray-700 rounded-lg p-0.5">
            {materials.map((m) => {
              const active = mat === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMat(m)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    active
                      ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {fmtMat(m)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2.5 px-5 font-medium">Rank</th>
                <th className="py-2.5 px-3 font-medium">Country</th>
                <th className="py-2.5 px-3 font-medium">Score</th>
                <th className="py-2.5 px-3 font-medium">Partner risk</th>
                <th className="py-2.5 px-3 font-medium">Capacity</th>
                <th className="py-2.5 px-3 font-medium">Chokepoint</th>
                <th className="py-2.5 px-3 font-medium">Price</th>
                <th className="py-2.5 px-5 font-medium">Lead time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {rows.map((r) => (
                <tr key={`${r.material_id}-${r.country_name}`}>
                  <td className="py-3 px-5 tabular-nums text-gray-500 dark:text-gray-500">
                    {String(r.rank).padStart(2, '0')}
                  </td>
                  <td className="py-3 px-3 font-medium text-gray-900 dark:text-white">
                    {r.country_name}
                  </td>
                  <td className="py-3 px-3 min-w-[120px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 max-w-[80px] h-1 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-900 dark:bg-white"
                          style={{ width: `${r.composite_score}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-700 dark:text-gray-300 tabular-nums">
                        {r.composite_score}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                    {r.partner_risk_score_fsi_wgi}
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-600 dark:text-gray-400">
                    {r.export_capacity}
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-600 dark:text-gray-400">
                    {r.chokepoint_exposure}
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-600 dark:text-gray-400">
                    {r.price_competitiveness}
                  </td>
                  <td className="py-3 px-5 text-xs text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">
                    {r.estimated_lead_time_months} mo
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            India domestic sourcing vs AURIC demand
          </h3>
          <span className="text-[11px] text-gray-500 dark:text-gray-500">
            {india.length} sources
          </span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {india.map((d) => (
            <div
              key={`${d.material_id}-${d.state}`}
              className="border border-gray-200 dark:border-gray-800 rounded-lg p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {fmtMat(d.material_id)}
                </div>
                <Tag>{d.state}</Tag>
              </div>
              <div className="mt-3 space-y-1.5 text-xs">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 dark:text-gray-500">Status</span>
                  <span className="text-gray-900 dark:text-white">{d.extraction_status}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 dark:text-gray-500">Current output</span>
                  <span className="text-gray-900 dark:text-white tabular-nums">
                    {d.current_output}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 dark:text-gray-500">Ather demand</span>
                  <span className="text-gray-900 dark:text-white tabular-nums">
                    {d.ather_demand_at_auric}
                  </span>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800 text-xs">
                <span className="text-gray-500 dark:text-gray-500">Gap · </span>
                <span className="text-gray-900 dark:text-white">{d.gap_vs_demand}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </section>
  );
};

// ----------------------------------------------------------------------------
// SECTION 5 — Shipping
// ----------------------------------------------------------------------------
const ShippingSection = () => {
  const screen = screenById('screen_5');
  const routeColl = getCollection('screen_5', 'shipping_route_risk_monitor');
  const routes = routeColl?.sample_data || [];
  const ports = routeColl?.india_receiving_ports || [];
  const imports = sample('screen_5', 'ather_import_routes');
  const maxShare = Math.max(...imports.map((i) => i.share_of_imports_pct), 1);

  return (
    <section id="shipping" className="scroll-mt-24">
      <SectionHeader screen={screen} />

      <div className="grid lg:grid-cols-3 gap-3 mb-4">
        {routes.map((r) => (
          <Card key={r.route_id} className="p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <MutedLabel>Route</MutedLabel>
                <h4 className="font-semibold text-gray-900 dark:text-white text-base mt-1.5">
                  {r.route_name}
                </h4>
              </div>
              <Tag dot={RISK_DOT[r.risk_level]}>{RISK_LABEL[r.risk_level]}</Tag>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 leading-relaxed">
              {r.disruption_summary}
            </p>
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800 grid grid-cols-2 gap-3">
              <div>
                <MutedLabel>Ather imports</MutedLabel>
                <div className="text-xl font-semibold text-gray-900 dark:text-white mt-1 tabular-nums">
                  {r.ather_imports_share_pct}%
                </div>
              </div>
              <div>
                <MutedLabel>Updated</MutedLabel>
                <div className="text-xl font-semibold text-gray-900 dark:text-white mt-1">
                  {r.last_updated}
                </div>
              </div>
            </div>
            {r.material_ids_affected?.length ? (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                <MutedLabel className="mb-2">Materials affected</MutedLabel>
                <div className="flex flex-wrap gap-1">
                  {r.material_ids_affected.map((m) => (
                    <Tag key={m}>{fmtMat(m)}</Tag>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Ather import lanes
          </h3>
          <span className="text-[11px] text-gray-500 dark:text-gray-500">
            Receiving · {ports.join(', ')}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2.5 px-5 font-medium">Material</th>
                <th className="py-2.5 px-3 font-medium">Supplier country</th>
                <th className="py-2.5 px-3 font-medium">Route</th>
                <th className="py-2.5 px-3 font-medium">Loading</th>
                <th className="py-2.5 px-3 font-medium">Discharge</th>
                <th className="py-2.5 px-5 font-medium">% imports</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {imports.map((r, idx) => (
                <tr key={`${r.route_id}-${r.material_id}-${idx}`}>
                  <td className="py-3 px-5 font-medium text-gray-900 dark:text-white">
                    {fmtMat(r.material_id)}
                  </td>
                  <td className="py-3 px-3 text-gray-700 dark:text-gray-300">
                    {r.supplier_country}
                  </td>
                  <td className="py-3 px-3 text-xs text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    {r.route_id}
                  </td>
                  <td className="py-3 px-3 text-gray-700 dark:text-gray-300">{r.port_of_loading}</td>
                  <td className="py-3 px-3 text-gray-700 dark:text-gray-300">
                    {r.port_of_discharge}
                  </td>
                  <td className="py-3 px-5 min-w-[120px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 max-w-[80px] h-1 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-900 dark:bg-white"
                          style={{ width: `${(r.share_of_imports_pct / maxShare) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-900 dark:text-white tabular-nums">
                        {r.share_of_imports_pct}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
};

// ----------------------------------------------------------------------------
// SECTION 6 — Competitors
// ----------------------------------------------------------------------------
const CompetitorsSection = () => {
  const screen = screenById('screen_6');
  const signals = sample('screen_6', 'competitor_supply_chain_signals');

  return (
    <section id="competitors" className="scroll-mt-24">
      <SectionHeader screen={screen} />
      <Card>
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Competitor signal feed
          </h3>
          <div className="flex items-center gap-1.5">
            {data.competitors.map((c) => (
              <Tag key={c.id}>{c.label}</Tag>
            ))}
          </div>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-800">
          {signals.map((s) => {
            const competitor = COMPETITOR_LOOKUP[s.competitor_id];
            return (
              <div key={s.signal_id} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-semibold border border-gray-200 dark:border-gray-700 flex-shrink-0">
                    {competitor?.label?.[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-500 mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {competitor?.label}
                      </span>
                      <span>·</span>
                      <span>{SIGNAL_LABEL[s.signal_type] || formatLabel(s.signal_type)}</span>
                      <span>·</span>
                      <span className="tabular-nums">{s.date}</span>
                    </div>
                    <h4 className="font-medium text-gray-900 dark:text-white text-[15px]">
                      {s.headline}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                      {s.summary}
                    </p>
                    <div className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                      Market impact · {s.market_impact_note}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                      {s.material_ids?.map((m) => (
                        <Tag key={m}>{fmtMat(m)}</Tag>
                      ))}
                      {s.supplier_or_partner_named ? (
                        <Tag>Partner · {s.supplier_or_partner_named}</Tag>
                      ) : null}
                      {s.deal_duration_years ? <Tag>{s.deal_duration_years}-yr deal</Tag> : null}
                      <Tag>{s.source_name}</Tag>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </section>
  );
};

// ----------------------------------------------------------------------------
// SECTION 7 — Scenarios
// ----------------------------------------------------------------------------
const ScenariosSection = () => {
  const screen = screenById('screen_7');
  const scenarios = sample('screen_7', 'disruption_scenarios');
  const baseline = sample('screen_7', 'ather_scenario_baseline');
  const [active, setActive] = useState(0);
  const s = scenarios[active];

  return (
    <section id="scenarios" className="scroll-mt-24">
      <SectionHeader screen={screen} />

      <Card className="p-5 mb-4">
        <MutedLabel className="mb-3">Choose a disruption scenario</MutedLabel>
        <div className="flex flex-wrap gap-2">
          {scenarios.map((sc, i) => (
            <button
              key={sc.scenario_id}
              type="button"
              onClick={() => setActive(i)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                active === i
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700 dark:hover:border-gray-500'
              }`}
            >
              {sc.scenario_name}
            </button>
          ))}
        </div>
      </Card>

      {s ? (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="p-4">
              <MutedLabel>Supply affected</MutedLabel>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
                {s.supply_affected_pct}%
              </div>
            </Card>
            <Card className="p-4">
              <MutedLabel>Alternative cover</MutedLabel>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
                {s.alternative_cover_pct}%
              </div>
            </Card>
            <Card className="p-4">
              <MutedLabel>Annual cost impact</MutedLabel>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
                {fmtInrCr(s.annual_cost_increase_inr_cr)}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-500 mt-1 tabular-nums">
                +{s.price_premium_pct_min}–{s.price_premium_pct_max}% premium
              </div>
            </Card>
            <Card className="p-4">
              <MutedLabel>Lead time</MutedLabel>
              <div className="text-3xl font-semibold text-gray-900 dark:text-white mt-2 tabular-nums">
                {s.lead_time_to_new_supply_months_min === s.lead_time_to_new_supply_months_max
                  ? `${s.lead_time_to_new_supply_months_min} mo`
                  : `${s.lead_time_to_new_supply_months_min}–${s.lead_time_to_new_supply_months_max} mo`}
              </div>
            </Card>
          </div>

          <Card className="p-5 border-l-2 border-l-gray-900 dark:border-l-white">
            <MutedLabel className="mb-2">Scenario projection</MutedLabel>
            <p className="text-[15px] text-gray-800 dark:text-gray-200 leading-relaxed">
              {s.narrative_output}
            </p>
            {s.alternative_countries?.length ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-gray-500 dark:text-gray-500">
                  Alternative sources
                </span>
                {s.alternative_countries.map((c) => (
                  <Tag key={c}>{c}</Tag>
                ))}
              </div>
            ) : null}
          </Card>
        </div>
      ) : null}

      <Card className="mt-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Ather BOM baseline · scenario inputs
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2.5 px-5 font-medium">Material</th>
                <th className="py-2.5 px-3 font-medium">Primary source</th>
                <th className="py-2.5 px-3 font-medium">Share</th>
                <th className="py-2.5 px-3 font-medium">Annual spend</th>
                <th className="py-2.5 px-3 font-medium">Buffer</th>
                <th className="py-2.5 px-5 font-medium">Alternatives</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {baseline.map((b) => (
                <tr key={b.material_id}>
                  <td className="py-3 px-5 font-medium text-gray-900 dark:text-white">
                    {fmtMat(b.material_id)}
                  </td>
                  <td className="py-3 px-3 text-gray-700 dark:text-gray-300">
                    {b.primary_source_country}
                  </td>
                  <td className="py-3 px-3 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 max-w-[100px] h-1 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-900 dark:bg-white"
                          style={{ width: `${b.primary_supplier_share_pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-700 dark:text-gray-300 tabular-nums">
                        {b.primary_supplier_share_pct}%
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-gray-900 dark:text-white font-medium tabular-nums">
                    {fmtInrCr(b.annual_spend_inr_cr)}
                  </td>
                  <td className="py-3 px-3 text-gray-600 dark:text-gray-400 tabular-nums">
                    {b.buffer_days} days
                  </td>
                  <td className="py-3 px-5">
                    <div className="flex flex-wrap gap-1">
                      {b.alternative_sources?.map((alt) => (
                        <Tag key={alt}>{alt}</Tag>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </section>
  );
};

/** Tab id → screen section(s) for Corporate Command Centre */
export const CORPORATE_USE_CASE_TAB_SCREENS = {
  command: ['hero', 'kpi', 'alerts'],
  costs: ['costs'],
  china: ['china'],
  planb: ['planb'],
  shipping: ['shipping'],
  competitors: ['competitors'],
  scenarios: ['scenarios'],
};

export function CorporateUseCaseScreenContent({ tabId, profile: profileProp }) {
  const profile = profileProp ?? loadCorporateProfile();
  const blocks = CORPORATE_USE_CASE_TAB_SCREENS[tabId] || [];

  return (
    <div className="space-y-8">
      {blocks.includes('hero') ? <Hero profile={profile} /> : null}
      {blocks.includes('kpi') ? <KpiStrip /> : null}
      {blocks.includes('alerts') ? <AlertsSection /> : null}
      {blocks.includes('costs') ? <CostsSection /> : null}
      {blocks.includes('china') ? <ChinaSection /> : null}
      {blocks.includes('planb') ? <PlanBSection /> : null}
      {blocks.includes('shipping') ? <ShippingSection /> : null}
      {blocks.includes('competitors') ? <CompetitorsSection /> : null}
      {blocks.includes('scenarios') ? <ScenariosSection /> : null}
    </div>
  );
}
